import { useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Container from 'react-bootstrap/Container';
import BsButton from 'react-bootstrap/Button';
import styles from './custom-filters.module.scss';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import ScreenerAppContext from '../../../../stores/screener/screener-app-context';
import Icon from '../../../atoms/icon';
import ProductTypeFilter from '../../../molecules/screener/product-type-filter';
import AssetClassFilter from '../../../molecules/screener/asset-class-filter';
import RangeSelector from '../../../molecules/screener/range-selector-filter';
import MStarSelector from '../../../molecules/screener/morningstar-rating-filter';
import Switch from '../../../molecules/screener/switch';
import StyleBox from '../../../molecules/screener/style-box';
import ViewResultButton from '../../../molecules/screener/view-result-button';
import { getWindowDimensions } from '../../../../utils/sharedHelpers';
import _debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import {
  showMyHoldings,
  showOnlyTIAAProduct,
  showFundsWithNullData
} from '../../../../stores/screener/screener-redux-store';
import ResetFiltersLink from '../../../molecules/screener/reset-filters-link';

import {
  CHANNELS,
  RANGE_SELECTOR_MODELS,
  STYLE_BOX_MODELS
} from '../../../../shared/screener-constants';

const className = augmentClassName(styles);

export const CustomFilters = ({
  expandPresetCategory,
  viewResult,
  setShowOwned,
  showOnlyOwnedFunds,
  showOnlyTIAA,
  setShowOnlyTIAA,
  showNullData,
  setShowNull,
  isSearchedPlanInContext
}) => {
  const { channel } = useContext(ScreenerAppContext);
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(getWindowDimensions().width < 768);
  const handleWindowResize = () => {
    setIsMobile(getWindowDimensions().width < 768);
  };

  useEffect(() => {
    const resize = _debounce(handleWindowResize, 150);
    window.addEventListener('resize', resize);

    return function cleanupListener() {
      window.removeEventListener('resize', resize);
    };
  }, [isMobile]);


  const slideDownPresetCategory = () => {
    expandPresetCategory();
  };

  const buttonText = expanded ? 'Less filters' : 'More filters';
  const chevron = expanded ? (
    <Icon.ChevronUp {...className('px-2')} height="15px" width="18px" />
  ) : (
    <Icon.ChevronDown {...className('px-2')} height="15px" width="18px" />
  );

  return (
    <div {...className('no-print')} role="form">
      <Row {...className('btn-row')}>
        <BsButton
          {...className('toggle-btn', 'btn-expand', 'btn--rounded')}
          // TIAA Analytics Tracking
          data-analytics-linkname="Browse_presets"
          data-analytics-section="component"
          data-analytics-componenttype="JumpNav"
          // END TIAA Analytics Tracking
          as={BsButton}
          size="lg"
          variant="outline-secondary"
          onClick={slideDownPresetCategory}
        >
          Browse presets
          <Icon.ChevronDown {...className('px-2')} height="15px" width="18px" />
        </BsButton>
      </Row>
      <Container fluid={'xl'}>
        <Col md={12} {...className('custom-filters-card')}>
          <Col id="custom-filters-card" {...className('pb-4')}>
            <Row {...className('px-4', 'py-6', 'justify-content-between', 'align-items-center')}>
              <div
                {...className('title')}
                // TIAA Analytics Tracking
                id="InvestmentScreenerCustomFilters"
                data-analytics-linkname="Custom_filters"
                data-analytics-section="component"
                data-analytics-componenttype="JumpNav"
              // END TIAA Analytics Tracking
              >
                {' '}
                Custom filters{' '}
              </div>
            </Row>
            {/* Mobile Filter Layout */}
            {isMobile ? (
              <Row {...className('top')}>
                <Col md={8} {...className('mobile-toggles')}>
                  <Row {...className('py-5')}>
                    {channel === CHANNELS.MYTIAA && (
                      <Switch
                        checked={showNullData}
                        onChange={() => setShowNull()}
                        label="Include results with no data for selected filters"
                        styleClass="w-100"
                      />
                    )}
                    {channel === CHANNELS.MYTIAA && (
                      <Switch
                        checked={showOnlyOwnedFunds}
                        onChange={() => setShowOwned()}
                        label="Show only my owned investments"
                        styleClass="w-100"
                      />
                    )}
                    {isSearchedPlanInContext && (
                      <Switch
                        checked={showOnlyTIAA}
                        onChange={() => setShowOnlyTIAA()}
                        label="Show only TIAA products"
                        styleClass="w-100"
                      />
                    )}
                  </Row>
                </Col>
                <Col md={4}>
                  <ProductTypeFilter />
                </Col>
                <Col md={4}>
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.PERFORMANCE} addMargin={false} />
                  </Row>
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.RISK} addMargin={false} />
                  </Row>
                  <Row {...className('py-6')}>
                    <MStarSelector addMargin={false} />
                  </Row>
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.EXPENSE} addMargin={false} />
                  </Row>
                </Col>
              </Row>
            ) : (
              <Row {...className('top')}>
                <Col md={4} {...className('section-side-space')}>
                  <ProductTypeFilter />
                </Col>
                <Col md={4} {...className('section-mid-space')}>
                  {channel === CHANNELS.MYTIAA && (
                    <Row {...className('py-5')}>
                      <Switch
                        checked={showOnlyOwnedFunds}
                        onChange={() => setShowOwned()}
                        label="Show only my owned investments"
                      />
                    </Row>
                  )}
                  {(channel === CHANNELS.DYNAMICMICROSITES || isSearchedPlanInContext) && (
                    <Row {...className('py-7')} />
                  )}
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.PERFORMANCE} />
                  </Row>
                  <Row {...className('py-6')}>
                    <MStarSelector />
                  </Row>
                </Col>
                <Col md={4} {...className('section-side-space')}>
                  {(isSearchedPlanInContext ||
                    channel === CHANNELS.MYTIAA ||
                    channel === CHANNELS.DYNAMICMICROSITES) && (
                    <Row {...className('py-5')}>
                      <Switch
                        checked={showOnlyTIAA}
                        onChange={() => setShowOnlyTIAA()}
                        label="Show only TIAA products"
                      />
                    </Row>
                  )}
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.RISK} />
                  </Row>
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.EXPENSE} />
                  </Row>
                  <Row {...className('py-4')}>
                    <Switch
                      checked={showNullData}
                      onChange={() => setShowNull()}
                      label="Include results with no data for selected filters"
                    />
                  </Row>
                </Col>
              </Row>
            )}
            {/*Expand Button Row*/}
            <Row {...className('justify-content-center', 'align-items-center')}>
              <hr />
              <BsButton
                {...className(
                  'btn-expand',
                  'btn--rounded',
                  'font-weight-bold',
                  'align-items-center', 'mb-2'
                )}
                // TIAA Analytics Tracking
                data-analytics-linkname={buttonText.replace(/ /g, '_')}
                data-analytics-section="component"
                data-analytics-componenttype="Button"
                // END TIAA Analytics Tracking
                variant="outline-secondary"
                size="md"
                onClick={() => setExpanded(!expanded)}
              >
                {buttonText}
                {chevron}
              </BsButton>
              <hr />
            </Row>
            {/*Expanded Filter Section*/}
            <div id="expanded-section" {...className('slide-class', 'py-2', expanded && 'expand')}>
              <Row>
                <Col md={4} {...className('section-side-space')}>
                  <AssetClassFilter />
                </Col>
                <Col md={4} {...className('section-mid-space')}>
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.PERF_VS_PEERS} />
                  </Row>
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.YIELD} />
                  </Row>
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.DAILY_YTD_RETURN} />
                  </Row>
                </Col>
                <Col md={4} {...className('section-side-space')}>
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.RISK_VS_PEERS} />
                  </Row>
                  <Row {...className('py-6')}>
                    <RangeSelector modelKey={RANGE_SELECTOR_MODELS.INCEPTION_DATE} />
                  </Row>
                </Col>
              </Row>
              <Row {...className('filters-section')}>
                <Col md={4}></Col>
                <Col md={4}>
                  <Row {...className('py-6')}>
                    <StyleBox modelKey={STYLE_BOX_MODELS.EQUITIES} />
                  </Row>
                </Col>
                <Col md={4}>
                  <Row {...className('py-6')}>
                    <StyleBox modelKey={STYLE_BOX_MODELS.BONDS} />
                  </Row>
                </Col>
              </Row>
            </div>
          </Col>
          <Col {...className('py-5')}>
            <Row {...className('align-items-center')} id="custom-filters-footer">
              <Col>
                <ResetFiltersLink />
              </Col>
              <Col {...className('col-6')}></Col>
              <Col {...className('text-right')}>
                <ViewResultButton viewResult={viewResult} />
              </Col>
            </Row>
          </Col>
        </Col>
      </Container>
    </div>
  );
};

CustomFilters.propTypes = {
  expandPresetCategory: PropTypes.func,
  viewResult: PropTypes.func,
  setShowOwned: PropTypes.func,
  setShowOnlyTIAA: PropTypes.func,
  setShowNull: PropTypes.func,
  showOnlyOwnedFunds: PropTypes.bool,
  showOnlyTIAA: PropTypes.bool,
  showNullData: PropTypes.bool,
  isSearchedPlanInContext: PropTypes.bool
};

const mapStateToProps = state => {
  return {
    showOnlyOwnedFunds: state.filterReducer.toggles.showOnlyMyHoldings,
    showOnlyTIAA: state.filterReducer.filters.showOnlyTIAA,
    showNullData: state.filterReducer.filters.showFundsWithNullData,
    isSearchedPlanInContext: state.filterReducer.inputs.isSearchedPlanInContext
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setShowOwned: () => dispatch(showMyHoldings()),
    setShowOnlyTIAA: () => dispatch(showOnlyTIAAProduct()),
    setShowNull: () => dispatch(showFundsWithNullData())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomFilters);
